import { cacheDB } from "./cache.db";
import { online$ } from "./cache.events";
import type { CachedResourceStrategy, ICacheEntry, ISetCacheOptions } from "./cache.types";

const SEPARATOR = "::";

export function buildCacheKey(namespace: string, key: string) {
  return `${namespace}${SEPARATOR}${key}`;
}

export async function setCache<T>(
  namespace: string,
  key: string,
  data: T,
  options: ISetCacheOptions = {},
): Promise<void> {
  const entry: ICacheEntry<T> = {
    key: buildCacheKey(namespace, key),
    namespace,
    data,
    updatedAt: Date.now(),
    ttl: options.ttl,
  };
  await cacheDB.entries.put(entry as ICacheEntry);
}

export async function getCache<T>(
  namespace: string,
  key: string,
): Promise<ICacheEntry<T> | undefined> {
  const entry = await cacheDB.entries.get(buildCacheKey(namespace, key));
  return entry as ICacheEntry<T> | undefined;
}

export async function deleteCache(namespace: string, key: string): Promise<void> {
  await cacheDB.entries.delete(buildCacheKey(namespace, key));
}

export async function clearNamespace(namespace: string): Promise<void> {
  await cacheDB.entries.where("namespace").equals(namespace).delete();
}

export async function clearCache(): Promise<void> {
  await cacheDB.entries.clear();
}

export function isCacheEntryStale(entry: ICacheEntry, ttlOverride?: number): boolean {
  const ttl = ttlOverride ?? entry.ttl;
  if (!ttl) return false;
  return Date.now() - entry.updatedAt > ttl;
}

export interface IFetchThroughCacheOptions {
  ttl?: number;
  /** Defaults to "stale-while-revalidate" — fresh when online, cached offline. */
  strategy?: CachedResourceStrategy;
}

/**
 * Imperative counterpart to `useCachedResource` for use outside component setup
 * (e.g. inside a watcher resolving a route param). Keeps requests fresh when
 * online and falls back to the on-device cache when offline or when the network
 * fails, so cache-dependent flows keep working without a connection.
 */
export async function fetchThroughCache<T>(
  namespace: string,
  key: string,
  fetcher: () => Promise<T>,
  options: IFetchThroughCacheOptions = {},
): Promise<T> {
  const cached = await getCache<T>(namespace, key);

  // Offline — serve the cached copy if we have one, otherwise let the fetcher
  // run (and likely reject) so the caller's error path still fires.
  if (!online$.value) {
    if (cached) return cached.data;
    return fetcher();
  }

  // Online but fresh enough under "cache-first" — skip the network entirely.
  if (options.strategy === "cache-first" && cached && !isCacheEntryStale(cached, options.ttl)) {
    return cached.data;
  }

  try {
    const fresh = await fetcher();
    await setCache(namespace, key, fresh, { ttl: options.ttl });
    return fresh;
  } catch (error) {
    // Network failed while nominally online — degrade to cache when possible.
    if (cached) return cached.data;
    throw error;
  }
}
